import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, Paper, Stack, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Button, Avatar, Chip, Alert,
  Card, CardContent, Divider, useTheme, alpha,
  Backdrop, CircularProgress, Snackbar,
  IconButton, Tooltip,
  ListSubheader,
  Divider as MuiDivider
} from '@mui/material';
import {
  Add as AddIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Notes as NotesIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Restaurant as SupervisorIcon,
  Waiter as WaiterIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types based on API response
interface InventoryItem {
  id: number;
  item_id: string;
  item_name: string;
  total_qty: number;
  display_name?: string;
}

interface ApiResponse {
  status: boolean;
  message: string;
  data: {
    items: InventoryItem[];
  };
}

interface Staff {
  user_id: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  type: 'waiter' | 'supervisor';
}

const AddInventory: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  
  // Form states
  const [itemId, setItemId] = useState<string>('');
  const [itemName, setItemName] = useState<string>('');
  const [qtyAdded, setQtyAdded] = useState<string>('');
  const [addedBy, setAddedBy] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  
  // Data stores
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [waiters, setWaiters] = useState<Staff[]>([]);
  const [supervisors, setSupervisors] = useState<Staff[]>([]);
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  
  // Custom item input
  const [isCustomItem, setIsCustomItem] = useState<boolean>(false);
  const [customItemName, setCustomItemName] = useState<string>('');
  
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch inventory items from the correct endpoint
  const fetchInventoryItems = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post<ApiResponse>(
        `${baseUrl()}BeverageSupervisors/getInventoryItems`, 
        formData, 
        config
      );
      
      console.log("Inventory items response:", response.data);

      if (response.data.status) {
        // Access the items array from data.items
        const items = response.data.data?.items || [];
        setInventoryItems(items);
      } else {
        showNotification('Failed to fetch inventory items', 'error');
      }
    } catch (error) {
      console.error("Fetch inventory items error:", error);
      showNotification('Error fetching inventory items', 'error');
    }
  };

  // Fetch waiters
  const fetchWaiters = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/getWaiters`, formData, config);
      
      if (response.data.status) {
        const waitersData = response.data.data || [];
        const formattedWaiters = waitersData.map((waiter: any) => ({
          ...waiter,
          fullName: waiter.fullName || `${waiter.firstName || ''} ${waiter.lastName || ''}`.trim(),
          type: 'waiter' as const
        }));
        setWaiters(formattedWaiters);
      }
    } catch (error) {
      console.error("Fetch waiters error:", error);
    }
  };

  // Fetch food & beverage supervisors
  const fetchSupervisors = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/getFoodBeverageSupervisors`, formData, config);
      
      console.log("Supervisors response:", response.data);

      if (response.data.status) {
        const supervisorsData = response.data.data || [];
        const formattedSupervisors = supervisorsData.map((sup: any) => ({
          ...sup,
          fullName: sup.fullName || `${sup.firstName || ''} ${sup.lastName || ''}`.trim(),
          type: 'supervisor' as const
        }));
        setSupervisors(formattedSupervisors);
      }
    } catch (error) {
      console.error("Fetch supervisors error:", error);
    }
  };

  // Combine all staff for the dropdown
  useEffect(() => {
    const combined = [...waiters, ...supervisors];
    setAllStaff(combined);
  }, [waiters, supervisors]);

  // Handle item selection
  const handleItemChange = (event: any) => {
    const selectedId = event.target.value;
    setItemId(selectedId);
    
    if (selectedId === 'custom') {
      setIsCustomItem(true);
      setItemName('');
    } else {
      setIsCustomItem(false);
      const selected = inventoryItems.find(item => item.item_id === selectedId);
      setItemName(selected?.item_name || '');
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (isCustomItem && !customItemName) {
      showNotification('Please enter item name', 'warning');
      return;
    }
    
    if (!isCustomItem && !itemId) {
      showNotification('Please select an item', 'warning');
      return;
    }

    if (!qtyAdded || parseInt(qtyAdded) <= 0) {
      showNotification('Please enter valid quantity', 'warning');
      return;
    }

    if (!addedBy) {
      showNotification('Please select who added the item', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      // Generate custom ID or use existing
      const finalItemId = isCustomItem ? `custom_${Date.now()}` : itemId;
      const finalItemName = isCustomItem ? customItemName : itemName;
      
      formData.append("item_id", finalItemId);
      formData.append("item_name", finalItemName);
      formData.append("qty_added", qtyAdded);
      formData.append("added_by", addedBy);
      formData.append("location", location);
      formData.append("notes", notes);

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/addInventory`, formData, config);
      
      if (response.data.status) {
        showNotification('Inventory added successfully', 'success');
        resetForm();
        // Refresh the inventory items list to show updated stock
        await fetchInventoryItems();
      } else {
        showNotification(response.data.message || 'Failed to add inventory', 'error');
      }
    } catch (error: any) {
      console.error("Add inventory error:", error);
      showNotification(error.response?.data?.message || 'Error adding inventory', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setItemId('');
    setItemName('');
    setQtyAdded('');
    setAddedBy('');
    setLocation('');
    setNotes('');
    setIsCustomItem(false);
    setCustomItemName('');
  };

  // Get staff name
  const getStaffName = (staff: Staff): string => {
    return staff.fullName || `${staff.firstName || ''} ${staff.lastName || ''}`.trim();
  };

  // Get staff icon based on type
  const getStaffIcon = (type: string) => {
    return type === 'waiter' ? <PersonIcon /> : <SupervisorIcon />;
  };

  // Get staff role label
  const getStaffRole = (type: string): string => {
    return type === 'waiter' ? 'Waiter' : 'F&B Supervisor';
  };

  useEffect(() => {
    fetchInventoryItems();
    fetchWaiters();
    fetchSupervisors();
  }, []);

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
          
          {/* Header */}
          <Paper 
            elevation={0}
            sx={{ 
              borderBottom: `1px solid ${theme.palette.divider}`,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              bgcolor: 'background.paper'
            }}
          >
            <Container maxWidth="xl">
              <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                <IconButton onClick={() => history.goBack()}>
                  <BackIcon />
                </IconButton>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main
                  }}
                >
                  <AddIcon />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h5" fontWeight={600}>
                    Add Inventory
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add new items to department inventory
                  </Typography>
                </Box>
                <Tooltip title="Refresh Items">
                  <IconButton onClick={fetchInventoryItems} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Container>
          </Paper>

          <Container maxWidth="lg" sx={{ py: 3 }}>
            
            {/* Main Form */}
            <Paper sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Add New Stock
              </Typography>
              <Divider sx={{ mb: 4 }} />

              <Grid container spacing={3}>
                
                {/* Item Selection */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" disabled={isCustomItem}>
                    <InputLabel>Select Item</InputLabel>
                    <Select
                      value={itemId}
                      onChange={handleItemChange}
                      label="Select Item"
                    >
                      <MenuItem value="custom" sx={{ color: theme.palette.primary.main }}>
                        <em>+ Add Custom Item</em>
                      </MenuItem>
                      {inventoryItems.map(item => (
                        <MenuItem key={item.item_id} value={item.item_id}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                            <span>{item.item_name}</span>
                            <Chip 
                              label={`Stock: ${item.total_qty}`}
                              size="small"
                              color={item.total_qty > 0 ? 'success' : 'default'}
                              variant="outlined"
                              sx={{ ml: 2 }}
                            />
                          </Stack>
                        </MenuItem>
                      ))}
                      {inventoryItems.length === 0 && (
                        <MenuItem disabled>No items available</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Custom Item Name */}
                {isCustomItem && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Custom Item Name"
                      value={customItemName}
                      onChange={(e) => setCustomItemName(e.target.value)}
                      placeholder="Enter new item name"
                      required
                    />
                  </Grid>
                )}

                {/* Quantity */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Quantity Added"
                    type="number"
                    value={qtyAdded}
                    onChange={(e) => setQtyAdded(e.target.value)}
                    InputProps={{ inputProps: { min: 1 } }}
                    placeholder="Enter quantity"
                    required
                  />
                </Grid>

                {/* Added By - Combined Dropdown with Waiters and Supervisors */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Added By</InputLabel>
                    <Select
                      value={addedBy}
                      onChange={(e) => setAddedBy(e.target.value)}
                      label="Added By"
                      renderValue={(selected) => {
                        const staff = allStaff.find(s => s.user_id === selected);
                        return staff ? (
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 24, height: 24, bgcolor: staff.type === 'waiter' ? theme.palette.info.main : theme.palette.success.main }}>
                              {staff.type === 'waiter' ? <PersonIcon fontSize="small" /> : <SupervisorIcon fontSize="small" />}
                            </Avatar>
                            <span>{getStaffName(staff)}</span>
                            <Chip 
                              label={getStaffRole(staff.type)}
                              size="small"
                              sx={{ 
                                ml: 1,
                                bgcolor: staff.type === 'waiter' ? alpha(theme.palette.info.main, 0.1) : alpha(theme.palette.success.main, 0.1),
                                color: staff.type === 'waiter' ? theme.palette.info.main : theme.palette.success.main,
                                fontSize: '0.7rem',
                                height: 20
                              }}
                            />
                          </Stack>
                        ) : '';
                      }}
                    >
                      {/* Waiters Section */}
                      {waiters.length > 0 && [
                        <ListSubheader key="waiters-header" sx={{ bgcolor: 'transparent', fontWeight: 600, color: theme.palette.info.main }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <PersonIcon fontSize="small" />
                            <span>WAITERS</span>
                          </Stack>
                        </ListSubheader>,
                        ...waiters.map(waiter => (
                          <MenuItem key={waiter.user_id} value={waiter.user_id}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.info.main }}>
                                <PersonIcon fontSize="small" />
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1">
                                  {getStaffName(waiter)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Waiter
                                </Typography>
                              </Box>
                            </Stack>
                          </MenuItem>
                        )),
                        <MuiDivider key="waiters-divider" sx={{ my: 1 }} />
                      ]}

                      {/* Supervisors Section */}
                      {supervisors.length > 0 && [
                        <ListSubheader key="supervisors-header" sx={{ bgcolor: 'transparent', fontWeight: 600, color: theme.palette.success.main }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <SupervisorIcon fontSize="small" />
                            <span>FOOD & BEVERAGE SUPERVISORS</span>
                          </Stack>
                        </ListSubheader>,
                        ...supervisors.map(supervisor => (
                          <MenuItem key={supervisor.user_id} value={supervisor.user_id}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.success.main }}>
                                <SupervisorIcon fontSize="small" />
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1">
                                  {getStaffName(supervisor)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  F&B Supervisor
                                </Typography>
                              </Box>
                            </Stack>
                          </MenuItem>
                        ))
                      ]}

                      {allStaff.length === 0 && (
                        <MenuItem disabled>No staff available</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Location */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g., Main Bar, Store Room"
                    InputProps={{
                      startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                    }}
                  />
                </Grid>

                {/* Notes */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Notes (Optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any additional notes"
                    InputProps={{
                      startAdornment: <NotesIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                    }}
                  />
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button variant="outlined" onClick={resetForm}>
                      Reset
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<AddIcon />}
                      onClick={handleSubmit}
                      disabled={submitting}
                      size="large"
                    >
                      {submitting ? 'Adding...' : 'Add Inventory'}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>

            {/* Info Card */}
            <Paper sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <InfoIcon color="info" />
                <Typography variant="body2" color="text.secondary">
                  Adding inventory will automatically update the department stock. 
                  The system will create a new item if it doesn't exist.
                </Typography>
              </Stack>
            </Paper>
          </Container>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* Loading Overlay */}
          <Backdrop
            sx={{
              color: '#fff',
              zIndex: theme.zIndex.drawer + 1,
              backdropFilter: 'blur(3px)'
            }}
            open={submitting || loading}
          >
            <Stack spacing={2} alignItems="center">
              <CircularProgress color="inherit" />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {loading ? 'Loading...' : 'Adding inventory...'}
              </Typography>
            </Stack>
          </Backdrop>

        </Box>
      </IonContent>
    </IonPage>
  );
};

export default AddInventory;